/**
 * Utility functions to ensure data is properly serializable for Firebase and postMessage
 */

export const sanitizeForSerialization = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle primitive types
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForSerialization(item));
  }

  // Handle dates
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  // Handle File objects (convert to metadata)
  if (obj instanceof File) {
    return {
      _type: 'File',
      name: obj.name,
      size: obj.size,
      type: obj.type,
      lastModified: obj.lastModified
    };
  }

  // Handle Blob objects
  if (obj instanceof Blob) {
    return {
      _type: 'Blob',
      size: obj.size,
      type: obj.type
    };
  }

  // Check for other non-serializable objects
  if (obj instanceof Event ||
      obj instanceof Element ||
      obj instanceof Node ||
      obj instanceof Promise ||
      obj instanceof MessageEvent ||
      obj instanceof Map ||
      obj instanceof Set ||
      obj instanceof WeakMap ||
      obj instanceof WeakSet ||
      obj?.constructor?.name === 'File' ||
      obj?.constructor?.name === 'Event' ||
      obj?.constructor?.name === 'Promise' ||
      obj?.constructor?.name === 'MessageEvent' ||
      obj?.tagName !== undefined ||
      obj?.nodeType !== undefined ||
      obj?.preventDefault !== undefined ||
      obj?.then !== undefined) {
    console.warn('Removing non-serializable object:', obj?.constructor?.name || typeof obj);
    return {
      _type: 'NonSerializable',
      constructorName: obj?.constructor?.name || 'Unknown'
    };
  }

  // Handle regular objects
  const sanitized: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      try {
        // Skip functions and other non-serializable properties
        if (typeof obj[key] === 'function') {
          continue;
        }
        sanitized[key] = sanitizeForSerialization(obj[key]);
      } catch (error) {
        console.warn(`Error sanitizing property ${key}:`, error);
        sanitized[key] = null;
      }
    }
  }

  return sanitized;
};

export const validateSerializable = (obj: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  try {
    const sanitized = sanitizeForSerialization(obj);
    JSON.stringify(sanitized);
    return { isValid: true, errors: [] };
  } catch (error) {
    errors.push(`Serialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return { isValid: false, errors };
  }
};

export const deepCloneSerializable = <T>(obj: T): T => {
  try {
    const sanitized = sanitizeForSerialization(obj);
    return JSON.parse(JSON.stringify(sanitized));
  } catch (error) {
    console.error('Deep clone failed:', error);
    throw new Error('Failed to create serializable deep clone');
  }
};